import classNames from 'classnames';
import * as _ from 'lodash-es';
import { BlockEmbed } from 'quill/blots/block';
import type { Delta, Op, Parchment, Range } from 'quill/core';
import Quill from 'quill/core';
import type { BaseSyntheticEvent, MutableRefObject } from 'react';
import React, { memo, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import BlotErrorBoundaryFallback from './BlotErrorBoundaryFallback';
import type { WithQuillProps } from './QuillContext';
import { withQuill } from './QuillContext';
import type { ModuleConfig, PortalBlot } from './QuillEditor';
import QuillEditor from './QuillEditor';
import * as styles from './ReactQuill.module.scss';
import type { WithUIElementResizeProps } from './UIElementResizeContext';
import { withUIElementResize } from './UIElementResizeContext';
import { findBlotByNode } from './utils/findByNode';
import findClosestScrollingContainer from './utils/findClosestScrollingContainer';
import ErrorBoundary from '../components/ErrorBoundary';
import type { ErrorBoundaryMetadata } from '../components/ErrorBoundary';
import { isTouchDevice } from '../constants/os';

// For mocking
let isForcingDisableLazyLoad = false;
// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const useForceDisableLazyLoad = () => {
  const previousValue = useRef<boolean | undefined>(undefined);

  if (previousValue.current === undefined) {
    previousValue.current = isForcingDisableLazyLoad;
    isForcingDisableLazyLoad = true;
  }

  useEffect(
    () => () => {
      if (typeof previousValue.current === 'boolean') {
        isForcingDisableLazyLoad = previousValue.current;
      }
    },
    [],
  );
};

const shouldLazyLoadBlot = (
  blot: Parchment.Blot,
): blot is Parchment.Blot & { domNode: Element } =>
  !isForcingDisableLazyLoad &&
  blot.domNode instanceof Element &&
  'shouldLazyLoad' in blot.statics &&
  blot.statics.shouldLazyLoad === true;

export const getEditorControlsContainer = (quill: Quill) =>
  quill.container.parentElement?.parentElement;

const stubEvent = (event: BaseSyntheticEvent) => {
  event.preventDefault();
};

const escapeCSSCustomPropertyValue = (value: unknown) => {
  return typeof value === 'string' ? `"${value}"` : undefined;
};

interface BlotRendererProps {
  blot: PortalBlot;
  readOnly: boolean;
  selected: boolean;
}

const BlotRenderer = memo(({ blot, readOnly, selected }: BlotRendererProps) => {
  const renderFallback = ({ type }: ErrorBoundaryMetadata) => (
    <BlotErrorBoundaryFallback type={type} blot={blot} />
  );

  return (
    <ErrorBoundary
      name={`editor:${blot.statics.blotName}`}
      renderFallback={renderFallback}
    >
      {blot.renderPortal({ readOnly, selected })}
    </ErrorBoundary>
  );
});

export interface ReactQuillProps {
  autoHeight?: boolean;
  autoFocus?: boolean;
  quillRef?: MutableRefObject<Quill | null>;
  content?: Delta | Op[];
  formats: Parchment.RegistryDefinition[];
  moduleConfig?: ModuleConfig;
  theme?: string;
  noTitleLine?: boolean;
  titlePlaceholder?: string;
  contentPlaceholder?: string;
  onMount?: (quill: QuillEditor) => void;
  onPerformanceMeasure?: (durations: {
    applied: number;
    mounted: number;
  }) => void;
  onBlotComponentsInitialLoad?: (quill: Quill) => void;
  onSelectionChange?: (range: Range | null, yChanged?: boolean) => void;
  onTextChange?: (delta: Delta, oldContent: Delta, source: string) => void;
  onUploadError?: (message: string) => void;
  readOnly: boolean;
  containerClassName?: string;
  rootClassName?: string;
  smallTitleSize?: boolean;
  disallowOrphanFormats?: string[];
  preloadNearScrollingContainerBlots?: boolean;
}

interface Props
  extends ReactQuillProps,
    WithQuillProps,
    WithUIElementResizeProps {}
interface State {
  portalBlots: {
    [id: string]: PortalBlot;
  };
  suspendedPortalBlots: Set<string>;
}

class ReactQuill extends React.Component<Props, State> {
  quill?: QuillEditor;
  unmounting: boolean;
  container: HTMLElement | null;
  embedSelectionContainer: HTMLElement | null;
  intersectionObserver?: IntersectionObserver;

  constructor(props: Props) {
    super(props);
    this.state = {
      portalBlots: {},
      suspendedPortalBlots: new Set(),
    };

    this.unmounting = false;
    this.container = null;
    this.embedSelectionContainer = null;
  }

  componentDidMount() {
    this.unmounting = false;

    const {
      content,
      readOnly,
      quillRef,
      formats,
      moduleConfig,
      theme,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
      noTitleLine,
      onMount,
      onPerformanceMeasure,
      onBlotComponentsInitialLoad,
      onUploadError,
      quillContext: { ancestorQuill, editorMounted },
      rootClassName,
      containerClassName,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
      smallTitleSize,
      disallowOrphanFormats,
      preloadNearScrollingContainerBlots:
        shouldPreloadNearScrollingContainerBlots,
    } = this.props;

    const quillContainer = document.createElement('div');
    quillContainer.setAttribute('translate', 'no');
    quillContainer.setAttribute(
      'class',
      classNames(styles.quillContainer, containerClassName, {
        [styles.smallTitleSize]: smallTitleSize,
        'no-title-line': noTitleLine,
      }),
    );

    const startTimestamp = performance.now();
    let appliedTimestamp: number | undefined;

    const callOnBlotComponentsInitialLoad = _.once(() => {
      if (onPerformanceMeasure && appliedTimestamp) {
        const now = performance.now();
        onPerformanceMeasure({
          applied: appliedTimestamp - startTimestamp,
          mounted: now - startTimestamp,
        });
      }
      if (this.quill) {
        onBlotComponentsInitialLoad?.(this.quill);
      }
    });

    let batchedBlots: PortalBlot[] = [];
    let isHandleBlotMountedScheduled = false;
    this.quill = new QuillEditor(quillContainer, {
      content: content || [],
      formats:
        disallowOrphanFormats && ancestorQuill
          ? formats.filter((target) => {
              const type =
                'blotName' in target ? target.blotName : target.attrName;
              return (
                !type ||
                !disallowOrphanFormats.includes(type) ||
                ancestorQuill.scroll.query(type)
              );
            })
          : formats,
      readOnly,
      noTitleLine,
      moduleConfig,
      theme,
      rootClassName: classNames(rootClassName, styles.quillEditor),
      embedSelectionContainer: this.embedSelectionContainer,
      onUploadError,
      onBlotMounted: (blot: PortalBlot) => {
        batchedBlots.push(blot);
        if (!isHandleBlotMountedScheduled) {
          isHandleBlotMountedScheduled = true;
          _.defer(() => {
            isHandleBlotMountedScheduled = false;
            if (batchedBlots.length > 0) {
              this.onBlotMounted(batchedBlots, callOnBlotComponentsInitialLoad);
              batchedBlots = [];
            }
          });
        }
      },
      onBlotUnmounted: this.onBlotUnmounted.bind(this),
      onSelectionChange: this.handleSelectionChange.bind(this),
      onTextChange: this.handleTextChange.bind(this),
      onReadOnlyChange: this.handleReadOnlyChange.bind(this),
      onActiveBlotChange: this.handleActiveBlotChange.bind(this),
    });

    appliedTimestamp = performance.now();
    // During the initialization of Quill, blots that need to be rendered
    // using React are collected synchronously into the `batchedBlots` array.
    if (!batchedBlots.length) {
      callOnBlotComponentsInitialLoad();
    }

    this.container?.appendChild(quillContainer);

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        this.handleIntersectionEntries(entries);
      },
      shouldPreloadNearScrollingContainerBlots
        ? {
            root: findClosestScrollingContainer(this.container),
            rootMargin: '100px 0px 100px 0px',
          }
        : {},
    );

    this.handleAutoFocus();

    if (quillRef) {
      quillRef.current = this.quill;
    }

    editorMounted(this.quill);
    onMount?.(this.quill);

    window.addEventListener('beforeprint', this.handleBeforePrint);
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { props, quill, state } = this;

    if (props.content !== prevProps.content) {
      // @ts-expect-error
      quill?.setContents(props.content);
      quill?.history.clear();
    }

    if (props.readOnly !== prevProps.readOnly) {
      quill?.enable(!props.readOnly);
    }

    if (props.quillContext.readOnly !== prevProps.quillContext.readOnly) {
      this.handleAutoFocus();
    }

    if (prevState.suspendedPortalBlots !== state.suspendedPortalBlots) {
      this.props.uiElementResize();
    }
  }

  componentWillUnmount() {
    this.unmounting = true;
    this.props.quillContext.editorUnmounted();
    if (this.props.quillRef) {
      this.props.quillRef.current = null;
    }
    this.intersectionObserver?.disconnect();
    window.removeEventListener('beforeprint', this.handleBeforePrint);
    this.quill?.container?.remove();
  }

  private handleBeforePrint = () => {
    flushSync(() => {
      this.setState({ suspendedPortalBlots: new Set() });
    });
  };

  private handleIntersectionEntries(entries: IntersectionObserverEntry[]) {
    const targets = _.compact(
      entries.map((entry) => (entry.isIntersecting ? entry.target : null)),
    );
    targets.forEach((target) => {
      this.intersectionObserver?.unobserve(target);
    });
    const blots: PortalBlot[] = _.compact(
      targets.map((target) => findBlotByNode(target) as PortalBlot),
    );

    if (blots.length) {
      const newSuspendedPortalBlots = new Set(this.state.suspendedPortalBlots);
      blots.forEach((blot) => {
        newSuspendedPortalBlots.delete(blot.id);
      });
      this.setState({ suspendedPortalBlots: newSuspendedPortalBlots });
    }
  }

  private handleSelectionChange(range: Range | null, yChanged?: boolean) {
    const { quill } = this;
    if (!quill) return;
    const {
      quillContext: { selection, localSelection },
    } = this.props;
    if (range == null && selection.range == null) return;
    localSelection({
      range,
      formats: range ? quill.getFormat(range) : {},
    });

    this.props.onSelectionChange?.(range, yChanged);
  }

  private handleTextChange(delta: Delta, oldContent: Delta, source: string) {
    const { quill } = this;
    if (
      quill &&
      delta.ops.some(
        (op) =>
          op.retain && op.attributes && Object.keys(op.attributes).length > 0,
      )
    ) {
      const range = quill.getSelection();
      if (range) {
        this.handleSelectionChange(range, false);
      }
    }

    this.props.onTextChange?.(delta, oldContent, source);
  }

  private handleReadOnlyChange(readOnly: boolean) {
    this.props.quillContext.readOnlyChange(readOnly);
  }

  private handleActiveBlotChange(blot: Parchment.Blot | null) {
    this.props.quillContext.onActiveBlotChange(blot);
  }

  private onBlotMounted(blots: PortalBlot[], callback?: () => void) {
    if (this.unmounting) return;
    const { portalBlots, suspendedPortalBlots } = this.state;
    const newPortalBlots = { ...portalBlots };
    const newSuspendedPortalBlots = new Set(suspendedPortalBlots);

    blots.forEach((blot) => {
      newPortalBlots[blot.id] = blot;

      if (shouldLazyLoadBlot(blot) && this.intersectionObserver) {
        this.intersectionObserver.observe(blot.domNode);
        newSuspendedPortalBlots.add(blot.id);
      }
    });

    // onBlotMounted is called within defer, which after React 18 upgrade was subject to
    // automatic batching. This caused the render of the blot to happen later than current code expects.
    // For example, without it, we would try to focus the first table cell before the render happened,
    // which would not work. flushSync makes sure to maintain the previous behavior.
    flushSync(() => {
      this.setState(
        {
          portalBlots: newPortalBlots,
          suspendedPortalBlots:
            newSuspendedPortalBlots.size === suspendedPortalBlots.size
              ? suspendedPortalBlots
              : newSuspendedPortalBlots,
        },
        callback,
      );
    });
  }

  private onBlotUnmounted(unmountedBlot: PortalBlot) {
    if (this.unmounting) return;
    this.setState((state) => {
      return { portalBlots: _.omit(state.portalBlots, unmountedBlot.id) };
    });
    if (unmountedBlot.domNode instanceof Element) {
      this.intersectionObserver?.unobserve(unmountedBlot.domNode);
    }
  }

  private handleAutoFocus() {
    // Skip auto focus on touch screen devices to avoid bringing up the
    // software keyboard automatically, otherwise it may disturb users.
    // This is also iOS Safari's default behavior:
    // https://bugs.webkit.org/show_bug.cgi?id=190017#c0
    // Note: It'd be better to check the keyboard type directly,
    // but there does't seem to have a good way to do that.
    if (isTouchDevice) return;

    if (this.props.autoFocus) {
      this.quill?.activateSelection();
    }
  }

  private handleMouseDown(e: React.MouseEvent) {
    if (!this.quill || e.target !== this.quill.root || this.props.readOnly) {
      return;
    }

    const [lastLine] = this.quill.getLine(this.quill.getLength() - 1);
    const isLastLineEmbed = lastLine instanceof BlockEmbed;

    if (isLastLineEmbed) {
      const rect = lastLine.domNode.getBoundingClientRect();

      if (e.clientY > rect.bottom) {
        this.quill.insertText(this.quill.getLength(), '\n', Quill.sources.USER);
        this.quill.setSelection(
          { index: this.quill.getLength() - 1, length: 0 },
          Quill.sources.USER,
        );
      }
    }
  }

  render() {
    const {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
      autoHeight,
      contentPlaceholder,
      quillContext,
      readOnly,
      titlePlaceholder,
    } = this.props;
    return (
      <>
        <div
          onMouseDown={this.handleMouseDown.bind(this)}
          className={classNames(styles.container, {
            [styles.autoHeight]: autoHeight,
          })}
          style={{
            // @ts-expect-error
            '--quill-title-placeholder':
              escapeCSSCustomPropertyValue(titlePlaceholder),
            '--quill-content-placeholder':
              escapeCSSCustomPropertyValue(contentPlaceholder),
          }}
          ref={(ref) => {
            this.container = ref;
          }}
        >
          {_.map(this.state.portalBlots, (blot) =>
            this.state.suspendedPortalBlots.has(blot.id) ? null : (
              <BlotRenderer
                key={blot.id}
                blot={blot}
                readOnly={readOnly}
                selected={blot === quillContext.activeBlot}
              />
            ),
          )}
        </div>
        {/*
          We move the browser selection to this textarea when an embed blot is selected
          to make selection behavior consistent across browsers.

          For example, Safari doesn't allow a collapsed range if it contains a `<video/>`:
          https://codepen.io/luin/pen/ZEbgmPq, whereas it allows `<iframe/>`.
        */}
        <textarea
          ref={(ref) => {
            this.embedSelectionContainer = ref;
          }}
          className={styles.embedSelectionContainer}
          onBeforeInput={stubEvent}
          aria-hidden
          tabIndex={-1}
        />
      </>
    );
  }
}

export const QlContainer = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, ref) => (
  <div
    ref={ref}
    {...props}
    className={classNames(
      styles.quillContainer,
      'ql-container',
      props.className,
    )}
  />
));

export const QlEditor = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, ref) => (
  <div
    ref={ref}
    {...props}
    className={classNames(styles.quillEditor, 'ql-editor', props.className)}
  />
));

export default withUIElementResize(withQuill(ReactQuill));
